import { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { cn } from "../../../utils/classnames";
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import { getPageContents } from "../../../actions/admin/cmspage";
import { MailOutlineIcon, PenIcon } from "../../../components/icons";
import { Reveal, Stagger, StaggerItem } from "../../../components/motion";

const CAREER_BANNER_URL =
  "https://newtlfbucket.s3.ap-northeast-1.amazonaws.com/public_folder/cms-image/cms-career/banner.jpg";

const CHAUFFEUR_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSe_MVLMwir3aL3MCnLF4eKzKmYEFi-cpQT8xYQyRTVJFAYYCg/viewform?c=0&flr=0&vc=0&w=1";

// Lists inside CMS HTML get Figma's bullet indent and row spacing, and lose
// Bootstrap's default bottom margin. whitespace-normal stops the inherited
// `pre-line` from turning the newlines between <li> tags into blank rows.
const CMS_LIST_CLASSES =
  "tw:[&_ul]:whitespace-normal tw:[&_ul]:list-disc tw:[&_ul]:!mb-0 tw:[&_ul]:!pl-[1.5em] tw:[&_li:last-child]:!mb-0";
const LIST_ITEM_SPACING = {
  en: "tw:[&_li]:mb-[3.5px] tw:lg:[&_li]:mb-[3px]",
  jp: "tw:[&_li]:mb-[6px]",
};

/**
 * Text sizes that differ between the EN and JP Figma frames (JP headings use
 * Zen Old Mincho, which needs a smaller size and taller line height).
 */
const TYPE = {
  en: {
    heroTitle: "tw:text-[32px] tw:leading-[42px] tw:lg:text-[45px] tw:lg:leading-[58px]",
    introTitle: "tw:text-[22px] tw:leading-[26px] tw:lg:text-[26px] tw:lg:leading-[35px]",
    sectionTitle: "tw:text-[26px] tw:leading-[35px] tw:lg:text-[32px] tw:lg:leading-[42px]",
    positionTitle: "tw:text-[22px] tw:leading-[26px] tw:lg:text-[26px] tw:lg:leading-[35px]",
    instruction: "tw:text-[14px] tw:leading-[21px]",
    button: "tw:text-[14px] tw:leading-[21px] tw:lg:text-[15px] tw:lg:leading-[22px]",
  },
  jp: {
    heroTitle: "tw:text-[32px] tw:leading-[48px] tw:lg:text-[40px] tw:lg:leading-[60px]",
    introTitle: "tw:text-[22px] tw:leading-[32px]",
    sectionTitle: "tw:text-[24px] tw:leading-[36px] tw:lg:text-[28px] tw:lg:leading-[42px]",
    positionTitle: "tw:text-[22px] tw:leading-[32px]",
    instruction: "tw:text-[14px] tw:leading-[22.5px]",
    button: "tw:text-[14px] tw:leading-[22px] tw:lg:text-[15px] tw:lg:leading-[24px]",
  },
};

/** Picks the EN or JP entry from a { en, jp } map. */
const forLanguage = (map, isJapanese) => (isJapanese ? map.jp : map.en);

/** Thin rule used between sections and under every block label. */
const Divider = ({ className }) => (
  <div className={cn("tw:h-px tw:bg-[#F1F1F1]", className)} />
);

/** "01/", "02/" … shown above each position title. */
const formatPositionNumber = (index) => `${String(index + 1).padStart(2, "0")}/`;

const APPLY_BUTTON_STYLES = {
  // Chauffeur form is the main call to action, so it gets the dark fill.
  primary: "tw:!text-white tw:bg-[#212529] tw:border-[#212529] tw:hover:bg-[#3a3f44]",
  outline: "tw:!text-[#4E4E4E] tw:bg-white tw:border-[#212529] tw:hover:bg-[#F1F1F1]",
};

/** Full-width button inside the grey "how to apply" card. */
const ApplyButton = ({ href, icon, label, isJapanese, variant = "outline", openInNewTab = false }) => (
  <a
    href={href}
    target={openInNewTab ? "_blank" : undefined}
    rel={openInNewTab ? "noreferrer" : undefined}
    className={cn(
      "tw:!no-underline tw:flex tw:items-center tw:justify-center tw:gap-2 tw:h-[50px] tw:px-8 tw:border tw:rounded-[5px] tw:font-bold tw:transition-colors",
      forLanguage(TYPE, isJapanese).button,
      APPLY_BUTTON_STYLES[variant]
    )}
  >
    {icon}
    {label}
  </a>
);

/** Italic instruction line + its button. */
const ApplyOption = ({ instruction, isJapanese, ...buttonProps }) => (
  <StaggerItem className="tw:flex tw:flex-col tw:gap-3 tw:w-full">
    <div
      className={cn(
        "tw:text-center tw:text-[#4E4E4E]",
        // Figma sets the JP instructions upright; only EN is italic
        !isJapanese && "tw:italic",
        forLanguage(TYPE, isJapanese).instruction
      )}
    >
      {instruction}
    </div>
    <ApplyButton isJapanese={isJapanese} {...buttonProps} />
  </StaggerItem>
);

/** Icons are 18px on mobile and 20px on desktop, as in Figma. */
const APPLY_ICON_CLASSES = "tw:shrink-0 tw:size-[18px] tw:lg:size-5";

/** "Overview" / "Requirements" style block: label, short rule, CMS body. */
const PositionBlock = ({ label, body, isJapanese, dividerClassName, className }) => (
  <div className={cn("tw:flex tw:flex-col tw:gap-3 tw:lg:gap-[14px]", className)}>
    <div
      className={cn(
        "tw:text-[#282828] tw:text-[15px] tw:leading-[22px] tw:lg:text-[16px] tw:lg:font-medium",
        isJapanese ? "tw:lg:leading-[24px]" : "tw:lg:leading-normal"
      )}
      dangerouslySetInnerHTML={{ __html: label }}
    />
    <Divider className={cn("tw:w-[88px]", dividerClassName)} />
    <div
      className={cn(
        "tw:text-[#282828] tw:text-[14px] tw:lg:text-[15px]",
        isJapanese ? "tw:leading-[22px] tw:lg:leading-[24px]" : "tw:leading-[21px] tw:lg:leading-[22px]",
        CMS_LIST_CLASSES,
        forLanguage(LIST_ITEM_SPACING, isJapanese)
      )}
      dangerouslySetInnerHTML={{ __html: body }}
    />
  </div>
);

/**
 * One open position: number + title on the left (desktop) / on top with a
 * full-width rule (mobile), detail blocks on the right.
 */
const PositionRow = ({ index, title, children, isJapanese, isTwoColumn, dividerClassName }) => (
  <Reveal className="tw:flex tw:flex-col tw:gap-8 tw:lg:flex-row tw:lg:justify-between tw:lg:gap-x-[54px]">
    <div className="tw:flex tw:flex-col tw:gap-4 tw:lg:gap-[10px] tw:lg:w-[250px] tw:lg:shrink-0">
      <div className="tw:text-[#9A7B3F] tw:text-[12px] tw:leading-[17px]">
        {formatPositionNumber(index)}
      </div>
      <div
        className={cn(
          "tw:font-libre-caslon-display tw:text-[#282828]",
          forLanguage(TYPE, isJapanese).positionTitle
        )}
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {/* Mobile only: rule under the title */}
      <Divider className={cn("tw:w-full tw:lg:hidden", dividerClassName)} />
    </div>
    <div
      className={cn(
        "tw:flex tw:flex-col tw:gap-8 tw:lg:flex-1 tw:lg:max-w-[900px] tw:min-w-0",
        isTwoColumn ? "tw:lg:flex-row tw:lg:gap-[54px]" : "tw:lg:gap-9"
      )}
    >
      {children}
    </div>
  </Reveal>
);

/**
 * Full-bleed beige band behind every other position. The negative margin
 * cancels the page gutter so the colour reaches both screen edges.
 */
const HighlightBand = ({ children }) => (
  <div className="tw:bg-[#F6F4F0] tw:-mx-5 tw:px-5 tw:py-[42px] tw:lg:-mx-[83px] tw:lg:px-[83px] tw:lg:py-[68px]">
    {children}
  </div>
);

// Rules inside the beige band: Figma uses a warmer tone on mobile.
const BAND_DIVIDER_CLASSES = "tw:!bg-[#E7E2D8] tw:lg:!bg-[#F1F1F1]";

const Careers = () => {
  const { t, i18n } = useTranslation();
  const isJapanese = i18n.language?.toUpperCase() !== "EN";

  const page_name = "cms-careers";
  const dispatch = useDispatch();
  const { cmspage } = useSelector((state) => state.cmspage);

  useEffect(() => {
    dispatch(getPageContents(page_name));
  }, [dispatch]);

  /** Reads a CMS field (e.g. "content4") in the current language. */
  const cms = (key) =>
    isJapanese ? cmspage?.page_content_jp?.[key] : cmspage?.page_content_en?.[key];

  // Shared props so every block picks the same language rules.
  const blockProps = { isJapanese };
  const typography = forLanguage(TYPE, isJapanese);

  return (
    <FrontLayout>
      <div className="tw:relative tw:max-w-[1920px] tw:mx-auto tw:font-hanken-grotesk">
        {/* Hero banner */}
        <div className="tw:relative tw:h-[350px] tw:lg:h-[417px] tw:overflow-hidden">
          <img
            src={CAREER_BANNER_URL}
            alt="Careers Banner"
            className="tw:absolute tw:inset-0 tw:w-full tw:h-full tw:object-cover"
          />
          {/* Figma uses a different overlay angle on mobile and desktop */}
          <div className="tw:absolute tw:inset-0 tw:lg:hidden tw:bg-[linear-gradient(-20.35deg,rgba(33,37,41,0.5)_30.58%,rgba(33,37,41,0.25)_80.58%)]" />
          <div className="tw:absolute tw:inset-0 tw:hidden tw:lg:block tw:bg-[linear-gradient(76.88deg,#181818_0.73%,rgba(40,42,45,0.3)_33.69%)]" />

          <Stagger
            onMount
            className="tw:absolute tw:left-5 tw:right-5 tw:bottom-[27px] tw:lg:left-[83px] tw:lg:right-auto tw:lg:bottom-[59px] tw:flex tw:flex-col tw:gap-3 tw:lg:gap-[14px]"
          >
            <StaggerItem
              className="tw:text-[#D4B055] tw:text-[8px] tw:leading-[12px] tw:tracking-[3px] tw:font-semibold tw:uppercase"
              dangerouslySetInnerHTML={{ __html: cms("content1") }}
            />
            {/* One line on desktop (Figma), wraps to two lines on mobile.
                [&_br]:hidden drops any manual line break typed in the CMS. */}
            <StaggerItem
              className={cn(
                "tw:font-libre-caslon-display tw:text-white tw:font-normal tw:lg:whitespace-nowrap tw:lg:[&_br]:hidden",
                typography.heroTitle
              )}
              dangerouslySetInnerHTML={{ __html: cms("content2") }}
            />
          </Stagger>
        </div>

        <div className="tw:px-5 tw:lg:px-[83px] tw:mt-[42px] tw:lg:mt-[72px] tw:mb-[42px] tw:lg:mb-[72px]">
          {/* Intro copy */}
          <Reveal className="tw:flex tw:flex-col tw:gap-5 tw:lg:gap-4 tw:text-center">
            <div
              className={cn("tw:font-libre-caslon-display tw:text-[#282828]", typography.introTitle)}
              dangerouslySetInnerHTML={{ __html: cms("content4") }}
            />
            <div
              className={cn(
                "tw:text-[#4E4E4E] tw:text-[14px] tw:lg:text-[15px]",
                isJapanese ? "tw:leading-[22px] tw:lg:leading-[24px]" : "tw:leading-[21px] tw:lg:leading-[22px]"
              )}
              dangerouslySetInnerHTML={{ __html: cms("content5") }}
            />
          </Reveal>

          {/* How to apply */}
          <Reveal className="tw:mt-[42px] tw:lg:mt-8 tw:bg-[#F9F9F9] tw:rounded-[10px] tw:px-6 tw:py-7 tw:lg:p-12">
            <Stagger className="tw:flex tw:flex-col tw:gap-6 tw:lg:flex-row tw:lg:items-start tw:lg:gap-[54px]">
              <div className="tw:flex-1 tw:w-full">
                <ApplyOption
                  isJapanese={isJapanese}
                  instruction={t("CareerPage.Chauffeur.Instruction")}
                  href={CHAUFFEUR_FORM_URL}
                  openInNewTab
                  variant="primary"
                  icon={<PenIcon color="#FFFFFF" className={APPLY_ICON_CLASSES} />}
                  label={t("CareerPage.Chauffeur.ButtonText")}
                />
              </div>

              {/* Horizontal rule on mobile, vertical rule on desktop */}
              <Divider className="tw:w-full tw:lg:w-px tw:lg:h-[186px] tw:lg:shrink-0" />

              <div className="tw:flex-1 tw:w-full tw:flex tw:flex-col tw:gap-[30px] tw:lg:gap-6">
                <ApplyOption
                  isJapanese={isJapanese}
                  instruction={t("CareerPage.Info.Instruction")}
                  href="mailto:info@theluxuryfleet.com"
                  icon={<MailOutlineIcon className={APPLY_ICON_CLASSES} />}
                  label={t("CareerPage.Info.ButtonText")}
                />
                <ApplyOption
                  isJapanese={isJapanese}
                  instruction={t("CareerPage.Internship.Instruction")}
                  href="mailto:internship@theluxuryfleet.com"
                  icon={<MailOutlineIcon className={APPLY_ICON_CLASSES} />}
                  label={t("CareerPage.Internship.ButtonText")}
                />
              </div>
            </Stagger>
          </Reveal>

          {/* Open positions */}
          <div className="tw:mt-[42px] tw:lg:mt-[68px]">
            <Reveal className="tw:flex tw:flex-col tw:gap-6 tw:lg:gap-8">
              <div className={cn("tw:font-libre-caslon-display tw:text-[#282828] tw:text-center", typography.sectionTitle)}>
                {t("CareerPage.OpenPositions")}
              </div>
              <Divider className="tw:w-full" />
            </Reveal>

            {/* No rules between positions: the beige band on the middle one separates them */}
            <div className="tw:flex tw:flex-col tw:gap-[42px] tw:lg:gap-[68px] tw:mt-[42px] tw:lg:mt-[68px]">
              {/* Full-time Planning Specialist */}
              <PositionRow {...blockProps} index={0} title={cms("content22")}>
                <PositionBlock {...blockProps} label={cms("content23")} body={cms("content24")} />
                <PositionBlock {...blockProps} label={cms("content25")} body={cms("content26")} />
                <PositionBlock {...blockProps} label={cms("content27")} body={cms("content28")} />
              </PositionRow>

              {/* Chauffeur */}
              <HighlightBand>
                <PositionRow {...blockProps} index={1} title={cms("content12")} isTwoColumn dividerClassName={BAND_DIVIDER_CLASSES}>
                  <PositionBlock {...blockProps} className="tw:flex-1" dividerClassName={BAND_DIVIDER_CLASSES} label={cms("content13")} body={cms("content14")} />
                  <PositionBlock {...blockProps} className="tw:flex-1" dividerClassName={BAND_DIVIDER_CLASSES} label={cms("content15")} body={cms("content16")} />
                </PositionRow>
              </HighlightBand>

              {/* Administration Executive */}
              <PositionRow {...blockProps} index={2} title={cms("content17")} isTwoColumn>
                <PositionBlock {...blockProps} className="tw:flex-1" label={cms("content18")} body={cms("content19")} />
                <PositionBlock {...blockProps} className="tw:flex-1" label={cms("content20")} body={cms("content21")} />
              </PositionRow>
            </div>
          </div>
        </div>
      </div>
    </FrontLayout>
  );
};

export default Careers;
